<script setup lang="ts">
/**
 * Dépôt de factures PDF (DEV-714, palier 3).
 *
 * Le client sélectionne 1 à 10 PDF → `store.uploadEtSuivre` les envoie à
 * api-metier (`POST /v5/energyinvoices/upload`), qui relaie à courtage (S3 +
 * parse asynchrone). La modale SUIT le job et affiche le résultat — UNIQUEMENT
 * des compteurs (« déposée + parsée »), jamais une anomalie (frontière R1).
 */
import { computed, ref } from "vue"
import { useEspaceFacturesStore } from "@modules/courtage/stores/espace-factures.store"
import type { FactureUploadJob } from "@modules/courtage/types/facture-espace"

defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
    (e: "update:modelValue", value: boolean): void
    (e: "done", job: FactureUploadJob): void
}>()

defineOptions({ name: "ModalDeposerFactures" })

const MAX_FICHIERS = 10
const store = useEspaceFacturesStore()

type Phase = "choix" | "envoi" | "fini" | "erreur"
const phase = ref<Phase>("choix")
const fichiers = ref<File[]>([])
const job = ref<FactureUploadJob | null>(null)
const messageErreur = ref<string | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

const enCours = computed(() => phase.value === "envoi")

function onPick(e: Event): void {
    const input = e.target as HTMLInputElement
    const picked = Array.from(input.files ?? []).filter(
        (f) =>
            f.type === "application/pdf" ||
            f.name.toLowerCase().endsWith(".pdf")
    )
    fichiers.value = picked.slice(0, MAX_FICHIERS)
    messageErreur.value =
        picked.length > MAX_FICHIERS
            ? `Maximum ${MAX_FICHIERS} fichiers par dépôt — seuls les ${MAX_FICHIERS} premiers sont retenus.`
            : null
}

function retirer(i: number): void {
    fichiers.value = fichiers.value.filter((_, idx) => idx !== i)
}

async function deposer(): Promise<void> {
    if (!fichiers.value.length) return
    phase.value = "envoi"
    messageErreur.value = null
    try {
        const res = await store.uploadEtSuivre(fichiers.value)
        job.value = res
        phase.value = "fini"
        emit("done", res)
    } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
            ?.status
        if (status === 429) {
            messageErreur.value =
                "Plafond de dépôts du jour atteint pour votre organisation. Réessayez demain."
        } else {
            const msg = err instanceof Error ? err.message : String(err)
            messageErreur.value = `Le dépôt a échoué : ${msg}`
        }
        phase.value = "erreur"
    }
}

function reset(): void {
    phase.value = "choix"
    fichiers.value = []
    job.value = null
    messageErreur.value = null
    if (inputRef.value) inputRef.value.value = ""
}

function close(): void {
    if (enCours.value) return // pas de fermeture pendant l'envoi
    reset()
    emit("update:modelValue", false)
}

const resume = computed(() => {
    const r = job.value?.resultats
    if (!r) return null
    return { factures: r.n_factures, erreurs: r.erreurs, pdf: r.n_pdf }
})
</script>

<template>
    <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
        <div class="w-full max-w-md rounded-xl bg-white shadow-xl">
            <header
                class="flex items-start justify-between gap-3 border-b border-neutral-100 px-6 py-4"
            >
                <h2 class="m-0 text-lg font-medium text-primary-900">
                    Déposer des factures
                </h2>
                <button
                    type="button"
                    class="text-neutral-400 hover:text-neutral-700 disabled:opacity-40"
                    aria-label="Fermer"
                    :disabled="enCours"
                    @click="close"
                >
                    <i class="pi pi-times" aria-hidden="true" />
                </button>
            </header>

            <div class="px-6 py-5">
                <!-- Choix des fichiers -->
                <template v-if="phase === 'choix' || phase === 'erreur'">
                    <label
                        class="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-200 px-4 py-8 text-center hover:border-primary-300 hover:bg-primary-50/40"
                    >
                        <i
                            class="pi pi-file-pdf text-2xl text-neutral-400"
                            aria-hidden="true"
                        />
                        <span class="text-sm font-medium text-neutral-700">
                            Choisir des PDF
                        </span>
                        <span class="text-xs text-neutral-400">
                            jusqu'à {{ MAX_FICHIERS }} factures
                        </span>
                        <input
                            ref="inputRef"
                            type="file"
                            accept="application/pdf,.pdf"
                            multiple
                            class="hidden"
                            @change="onPick"
                        />
                    </label>

                    <ul v-if="fichiers.length" class="mt-3 space-y-1.5">
                        <li
                            v-for="(f, i) in fichiers"
                            :key="i"
                            class="flex items-center justify-between gap-2 rounded-md bg-neutral-50 px-3 py-2 text-sm"
                        >
                            <span class="flex min-w-0 items-center gap-2">
                                <i
                                    class="pi pi-file-pdf text-neutral-400"
                                    aria-hidden="true"
                                />
                                <span class="truncate text-neutral-700">{{
                                    f.name
                                }}</span>
                            </span>
                            <button
                                type="button"
                                class="text-neutral-400 hover:text-red-600"
                                aria-label="Retirer"
                                @click="retirer(i)"
                            >
                                <i class="pi pi-times" aria-hidden="true" />
                            </button>
                        </li>
                    </ul>

                    <p
                        v-if="messageErreur"
                        class="mt-3 flex items-start gap-2 text-xs text-red-600"
                    >
                        <i
                            class="pi pi-exclamation-triangle mt-0.5"
                            aria-hidden="true"
                        />
                        <span>{{ messageErreur }}</span>
                    </p>

                    <p
                        class="mt-4 flex items-start gap-2 text-xs text-neutral-400"
                    >
                        <i
                            class="pi pi-info-circle mt-0.5"
                            aria-hidden="true"
                        />
                        <span>
                            Nous lisons vos factures pour les classer par
                            compteur. Le traitement peut prendre un moment.
                        </span>
                    </p>
                </template>

                <!-- Envoi / traitement -->
                <div
                    v-else-if="phase === 'envoi'"
                    class="flex flex-col items-center gap-3 py-8 text-center"
                >
                    <i
                        class="pi pi-spin pi-spinner text-2xl text-primary-600"
                        aria-hidden="true"
                    />
                    <p class="m-0 text-sm text-neutral-600">
                        Dépôt et lecture de {{ fichiers.length }} facture(s) en
                        cours…
                    </p>
                </div>

                <!-- Résultat -->
                <div v-else class="py-4 text-center">
                    <i
                        class="pi pi-check-circle text-3xl text-green-600"
                        aria-hidden="true"
                    />
                    <p class="mt-2 text-sm font-medium text-neutral-800">
                        {{ resume?.factures ?? 0 }} facture(s) déposée(s) et
                        lue(s).
                    </p>
                    <p
                        v-if="resume && resume.erreurs > 0"
                        class="mt-1 text-xs text-amber-600"
                    >
                        {{ resume.erreurs }} fichier(s) n'ont pas pu être lus.
                    </p>
                    <p class="mt-1 text-xs text-neutral-400">
                        Elles apparaissent maintenant dans votre liste.
                    </p>
                </div>
            </div>

            <footer
                class="flex justify-end gap-2 border-t border-neutral-100 px-6 py-4"
            >
                <button
                    v-if="phase !== 'fini'"
                    type="button"
                    class="rounded-md px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
                    :disabled="enCours"
                    @click="close"
                >
                    Annuler
                </button>
                <button
                    v-if="phase === 'choix' || phase === 'erreur'"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                    :disabled="!fichiers.length"
                    @click="deposer"
                >
                    <i class="pi pi-upload" aria-hidden="true" />
                    Déposer
                </button>
                <button
                    v-if="phase === 'fini'"
                    type="button"
                    class="rounded-md bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                    @click="close"
                >
                    Fermer
                </button>
            </footer>
        </div>
    </div>
</template>
